// ⚠️ AVTOMATIK KO'CHIRILGAN NUSXA — QO'L BILAN TAHRIRLAMANG.
// Manba: frontend/shared/format/format.money.ts
// Yangilash: panel papkasidan `node ../shared/sync.mjs`
// Qorovul: `npm run test:format` (uchala panelda) — nusxa manbadan
// bir bayt farq qilsa yiqiladi.
// Valyuta belgisi va MAJOR birlikdagi summa matni.
// Operator paneli (admin) va abonent kabineti (selfcare) uchun BIR XIL.
//
// ⚠️ `portal` ga KO'CHIRILMAYDI — o'lchangan sabab bilan: portal (mehmon
// sahifasi) pul ko'rsatmaydi (0 ta chaqiruv) va uning `i18n` da
// `units.currencies` bo'limi UMUMAN YO'Q. Ya'ni bu yerdagi kod portalga
// ko'chirilsa, u ishlamaydigan holda emas — YOMONROQ: jimgina BOSHQA
// natija berardi (qo'lda tarjima qatlami bo'sh, `Intl` ga tushib ketardi).
//
// ⚠️ MINOR/MAJOR CHEGARASI. Bu yerdagi `formatMoney` MAJOR birlikni
// (so'm, afg'oni) kutadi. Backend `long` da MINOR beradi
// (`02-LEDGER.md` §11) — uni ko'rsatish uchun `money.ts` dagi
// `formatMoneyValue` ishlatiladi, u aylantirishni va yaxlitlash qoidasini
// o'zi hal qiladi.

import i18n, { currentIntlLocale } from '@/app/i18n'

import { numberFormat } from './format.intl'

/**
 * Amaldagi valyuta kodi (ISO 4217) — serverdan keladi (`GET /api/me` yoki
 * `GET /api/selfcare/me`), u esa tenant sozlamasidan o'qiladi.
 *
 * BITTA TENANT = BITTA STANDART VALYUTA. Har summada alohida valyuta tanlash
 * ataylab qilinmagan: abonentda bitta pul balansi bor, ya'ni boshqa valyutadagi
 * paketni sotish kursni talab qilardi — kurs esa vaqt bo'yicha o'zgaradi va har
 * to'lov qaysi kursda hisoblangani saqlanishi kerak bo'lardi. Bu alohida quyi
 * tizim.
 *
 * ⚠️ MODUL DARAJASIDAGI QIYMAT REAKTIV EMAS. U o'zgarganda React hech narsani
 * qayta chizmaydi. Shuning uchun u ILDIZDA o'rnatiladi (admin — `AppLayout`,
 * selfcare — `AuthProvider`) va u yuklanmaguncha ichki sahifalar chizilmaydi —
 * aks holda summalar avval ZAXIRA valyutada chiqib, keyin sakrab o'zgaradi.
 * MVP'da aynan shunday bo'lgan edi: Sozlamalarda AFN tanlansa ham, boshqa
 * sahifa qayta yuklangach so'mda ko'rinardi.
 *
 * Zaxira qiymat UZS: konfiguratsiya yuklanmaguncha ham summa birliksiz qolmasin.
 */
let currencyCode = 'UZS'

export function setCurrencyCode(code: string | null | undefined): void {
  if (code && code.trim()) currencyCode = code.trim().toUpperCase()
}

export function getCurrencyCode(): string {
  return currencyCode
}

/**
 * Kirill harfi bormi — `Intl` ning o'zbekcha ma'lumotini tekshirish uchun.
 * O'zbek tili LOTIN yozuvida, shuning uchun kirill javob = noto'g'ri.
 */
const CYRILLIC = /[Ѐ-ӿ]/

/**
 * Valyuta belgisi `Intl` dan. Ishonchsiz bo'lsa null qaytaradi.
 *
 * NEGA ISHONCHSIZ BO'LISHI MUMKIN: Chromium'ning `uz-UZ` ICU ma'lumoti chala —
 * u UZS uchun "узбекский сум" (RUSCHA) qaytaradi, Node esa "soʻm". Ya'ni
 * `Intl` ga so'zsiz ishonib bo'lmaydi va buni faqat brauzerda sinaganda
 * ko'rish mumkin. `supportedLocalesOf` esa `uz-UZ` ni "qo'llab-quvvatlanadi"
 * deydi, ya'ni oldindan aniqlab ham bo'lmaydi. Shuning uchun:
 *   · kirill javob lotin o'zbek UI'sida rad etiladi
 *   · belgi kodning o'zi bo'lsa (masalan "KZT") foyda yo'q — null
 */
function intlSymbol(locale: string, code: string): string | null {
  try {
    const parts = new Intl.NumberFormat(locale, { style: 'currency', currency: code }).formatToParts(
      1,
    )
    const symbol = parts.find((p) => p.type === 'currency')?.value
    if (!symbol || symbol === code) return null
    if (locale.startsWith('uz') && CYRILLIC.test(symbol)) return null
    return symbol
  } catch {
    return null
  }
}

/**
 * Valyuta belgisi tanlangan tilda: UZS → "so'm" / "сум" / "UZS".
 *
 * UCH QATLAM, shu tartibda:
 *   1. Qo'lda tarjima (`units.currencies.<KOD>`) — eng ko'p ishlatiladigan
 *      valyutalar uchun. Bu qatlam `Intl` dan USTUN, chunki brauzerlarning
 *      ICU ma'lumoti bir xil emas (yuqoridagi izohga qarang)
 *   2. `Intl` belgisi — ro'yxatda yo'q valyutalar uchun ($, €, ₹ ...)
 *   3. Kodning o'zi — hech narsa topilmasa. Hech qachon birliksiz qolmaydi
 *
 * ⚠️ 1-qatlam MA'LUMOT, kod emas: u `src/app/i18n/*.json` da yotadi va
 * panellarda BIR XIL bo'lishi kod bilan kafolatlanmaydi. Farqlar
 * `tests/format-behavior.mjs` dagi `DATA_DRIFT` jadvalida qayd etilgan.
 */
export function currencySymbol(code: string, locale = currentIntlLocale()): string {
  const key = `units.currencies.${code}`
  const translated = i18n.t(key, { defaultValue: '' })
  if (translated) return translated
  return intlSymbol(locale, code) ?? code
}

/**
 * Pul summasi — son tilga mos ajratiladi, belgi ORQAGA qo'yiladi:
 * uz "50 000 so'm" · ru "50 000 сум" · en "50,000 UZS"
 *
 * NEGA `style:'currency'` EMAS: u belgini tilga qarab oldinga ham qo'yadi
 * ($50,000), lekin ICU ma'lumoti chala bo'lgan tilda (uz) natija chalkash
 * chiqadi. Doimiy suffiks — bashorat qilinadigan va uch tilda ham to'g'ri.
 *
 * ⚠️ ARGUMENT MAJOR BIRLIKDA (so'm, afg'oni) — MINOR EMAS. Backenddan
 * kelgan `Money` uchun `money.ts` dagi `formatMoneyValue` ishlatiladi.
 *
 * ⚠️ YAXLITLASH QOIDASI: `maximumFractionDigits: 0`, ya'ni kasr qismi
 * KO'RSATILMAYDI va son `Intl` ning standart rejimida (`halfExpand` —
 * yarimda noldan uzoqqa) yaxlitlanadi: 12.4 → "12", 12.5 → "13",
 * -12.5 → "-13". Bu FAQAT KO'RSATISH: hisob-kitob har doim minor
 * birlikdagi butun sonda boradi va bu yerga hech qachon qaytmaydi.
 */
export function formatMoney(
  amount: number | null | undefined,
  code: string = currencyCode,
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—'
  const locale = currentIntlLocale()
  const text = numberFormat(locale, { maximumFractionDigits: 0 }).format(amount)
  return `${text} ${currencySymbol(code, locale)}`
}

/**
 * Valyutaning to'liq nomi — sozlamalardagi ro'yxat uchun.
 * "O'zbekiston so'mi" / "узбекский сум" / "Uzbekistani Som".
 *
 * `Intl` ga tayanadi (barcha ISO 4217 kodlarini biladi), lekin lotin o'zbek
 * UI'sida kirill javob rad etiladi — u holda inglizcha nom ko'rsatiladi,
 * chunki kodning o'zidan ko'ra foydaliroq.
 */
export function currencyName(code: string, locale = currentIntlLocale()): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'currency' }).of(code)
    if (!name || name === code) return code
    if (locale.startsWith('uz') && CYRILLIC.test(name)) {
      return new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) ?? code
    }
    return name
  } catch {
    return code
  }
}
